'use client';

import { createContext, useContext, useCallback, useMemo, ReactNode, useState } from 'react';
import { DisclosureProps } from 'libs/contentful/types';
import { sortDisclosuresByPriority } from 'utils/disclosures';

interface DisclosureContextType {
  /** Every disclosure on the page, extracted server-side */
  allDisclosures: DisclosureProps[];
  /** Get the numbering map for embedded disclosures */
  getDisclosureNumberingMap: () => Map<string, number>;
  /** Get sorted disclosures for footer display */
  getSortedDisclosures: () => DisclosureProps[];
}

interface ConsumedDisclosuresContextType {
  /** Set of disclosure IDs that have been consumed by DisclosureAddOn modules */
  consumedDisclosureIds: Set<string>;
  /** Mark disclosures as consumed (shown in DisclosureAddOn) */
  consumeDisclosures: (moduleId: string, disclosureIds: string[]) => () => void;
  /** Check if a disclosure has been consumed */
  isDisclosureConsumed: (disclosureId: string) => boolean;
}

const DisclosureContext = createContext<DisclosureContextType | null>(null);
const ConsumedDisclosuresContext = createContext<ConsumedDisclosuresContextType | null>(null);

export interface DisclosureProviderProps {
  children: ReactNode;
  /** Every disclosure on the page, extracted server-side */
  initialDisclosures?: DisclosureProps[];
}

export function DisclosureProvider({ children, initialDisclosures = [] }: DisclosureProviderProps) {
  const [consumedRegistrations, setConsumedRegistrations] = useState<Map<string, string[]>>(new Map());

  // Compute consumed disclosure IDs from all DisclosureAddOn registrations
  const consumedDisclosureIds = useMemo(() => {
    const ids = new Set<string>();
    consumedRegistrations.forEach(disclosureIds => {
      disclosureIds.forEach(id => ids.add(id));
    });
    return ids;
  }, [consumedRegistrations]);

  // Mark disclosures as consumed (shown in DisclosureAddOn)
  const consumeDisclosures = useCallback((moduleId: string, disclosureIds: string[]) => {
    setConsumedRegistrations(prev => new Map(prev).set(moduleId, disclosureIds));

    // Return cleanup function
    return () => {
      setConsumedRegistrations(prev => {
        const newMap = new Map(prev);
        newMap.delete(moduleId);
        return newMap;
      });
    };
  }, []);

  // Check if a disclosure has been consumed
  const isDisclosureConsumed = useCallback(
    (disclosureId: string) => {
      return consumedDisclosureIds.has(disclosureId);
    },
    [consumedDisclosureIds],
  );

  const allDisclosures = useMemo(
    () =>
      initialDisclosures.filter(
        (disclosure, index, array) => array.findIndex(d => d.sys?.id === disclosure.sys?.id) === index,
      ),
    [initialDisclosures],
  );

  const sortedDisclosures = useMemo(() => sortDisclosuresByPriority(allDisclosures), [allDisclosures]);

  // Create numbering map for embedded disclosures
  const disclosureNumberingMap = useMemo(() => {
    const map = new Map<string, number>();
    let autoNumberCounter = 0;

    sortedDisclosures.forEach(disclosure => {
      // Only assign numbers to disclosures that don't have symbols
      if (!disclosure.symbol && disclosure.sys?.id) {
        autoNumberCounter++;
        map.set(disclosure.sys.id, autoNumberCounter);
      }
    });

    return map;
  }, [sortedDisclosures]);

  const getDisclosureNumberingMap = useCallback(() => disclosureNumberingMap, [disclosureNumberingMap]);
  const getSortedDisclosures = useCallback(() => sortedDisclosures, [sortedDisclosures]);

  // Every client rich-text renderer consumes this, so a change after mount re-renders all of them.
  const disclosureValue = useMemo(
    () => ({
      allDisclosures,
      getDisclosureNumberingMap,
      getSortedDisclosures,
    }),
    [allDisclosures, getDisclosureNumberingMap, getSortedDisclosures],
  );

  const consumedValue = useMemo(
    () => ({
      consumedDisclosureIds,
      consumeDisclosures,
      isDisclosureConsumed,
    }),
    [consumedDisclosureIds, consumeDisclosures, isDisclosureConsumed],
  );

  return (
    <DisclosureContext.Provider value={disclosureValue}>
      <ConsumedDisclosuresContext.Provider value={consumedValue}>{children}</ConsumedDisclosuresContext.Provider>
    </DisclosureContext.Provider>
  );
}

/**
 * Hook to access disclosure context.
 * Must be used within a DisclosureProvider component.
 *
 * @returns {DisclosureContextType} Object containing disclosure state and management functions
 * @throws {Error} If used outside of a DisclosureProvider
 */
export function useDisclosureContext() {
  const context = useContext(DisclosureContext);

  if (!context) {
    throw new Error('useDisclosureContext must be used within a DisclosureProvider');
  }

  return context;
}

/**
 * Hook to safely access disclosure context without throwing an error.
 * Returns undefined if used outside of a DisclosureProvider.
 *
 * @returns {DisclosureContextType | undefined} Object containing disclosure state and management functions or undefined
 */
export function useDisclosureContextSafe() {
  return useContext(DisclosureContext);
}

/**
 * Hook to get the disclosure numbering map for embedded disclosures.
 * Used by rich text components to show the correct numbers for embedded disclosures.
 * Returns an empty Map if used outside of a DisclosureProvider.
 *
 * @example
 * function RichTextCopy({ copy }) {
 *   const disclosureNumberingMap = useDisclosureNumberingMap();
 *
 *   return documentToReactComponents(copy.json, {
 *     renderNode: {
 *       [INLINES.EMBEDDED_ENTRY]: (node, children) =>
 *         renderEmbeddedEntry(node, children, copy, disclosureNumberingMap),
 *     },
 *   });
 * }
 */
export function useDisclosureNumberingMap(): Map<string, number> {
  const context = useDisclosureContextSafe();
  if (!context) {
    return new Map(); // Return empty map if no provider is available
  }
  return context.getDisclosureNumberingMap();
}

/**
 * Hook to access consumed disclosures context for DisclosureAddOn modules.
 * Used to mark disclosures as "consumed" so they don't appear in DynamicDisclosures.
 *
 * @returns {ConsumedDisclosuresContextType} Object containing consumed disclosure management functions
 */
export function useConsumedDisclosures(): ConsumedDisclosuresContextType {
  const context = useContext(ConsumedDisclosuresContext);
  if (!context) {
    // Return a no-op implementation if used outside provider
    return {
      consumedDisclosureIds: new Set(),
      consumeDisclosures: () => () => {},
      isDisclosureConsumed: () => false,
    };
  }
  return context;
}
